export type GroupTalkTemplateId = "management" | "project" | "professional" | "custom";

export type GroupTalkMemberId =
  | "mei"
  | "cmo"
  | "cto"
  | "cfo"
  | "coo"
  | "pm"
  | "tax"
  | "law"
  | "labor"
  | "customer"
  | "consultant";

export type GroupTalkStanceId =
  | "bold"
  | "careful"
  | "idea"
  | "realist"
  | "numbers"
  | "user_first"
  | "skeptic"
  | "contrarian";

export type GroupTalkTemplateConfig = {
  id: GroupTalkTemplateId;
  title: string;
  description: string;
  members: GroupTalkMemberId[];
};

export type GroupTalkMemberConfig = {
  id: GroupTalkMemberId;
  label: string;
  roleLabel: string;
  fixed?: boolean;
};

export type GroupTalkParticipant = {
  participantId: string;
  memberId: GroupTalkMemberId;
  stanceId: GroupTalkStanceId | null;
  fixed: boolean;
};

export type GroupTalkPreviewMember = {
  participantId: string;
  memberId: GroupTalkMemberId;
  label: string;
  roleLabel: string;
  stanceId: GroupTalkStanceId | null;
  stanceLabel: string | null;
  fixed: boolean;
};

export const groupTalkMemberOptions: GroupTalkMemberConfig[] = [
  { id: "mei", label: "メイ", roleLabel: "ファシリテータ", fixed: true },
  { id: "cmo", label: "CMO", roleLabel: "市場分析、ブランド戦略、CAC を見る" },
  { id: "cto", label: "CTO", roleLabel: "技術スタック、開発効率、技術負債を見る" },
  { id: "cfo", label: "CFO", roleLabel: "資金繰り、利益率、ROI、キャッシュフローを見る" },
  { id: "coo", label: "COO", roleLabel: "オペレーション効率、リソース配分、標準化を見る" },
  { id: "pm", label: "PM", roleLabel: "ユーザー体験、ロードマップ、優先順位を見る" },
  { id: "tax", label: "税理士", roleLabel: "節税、決算、税務リスク、資金調達支援を見る" },
  { id: "law", label: "弁護士", roleLabel: "契約、コンプライアンス、法的防衛、リスク回避を見る" },
  { id: "labor", label: "社労士", roleLabel: "労務手続き、助成金、雇用条件、社会保険を見る" },
  { id: "customer", label: "顧客", roleLabel: "市場ニーズ、支払い意欲、不満点、離脱可能性を見る" },
  { id: "consultant", label: "コンサル", roleLabel: "全体を整理し、論点を構造化して見る" },
];

export const groupTalkStanceOptions: { id: GroupTalkStanceId; label: string }[] = [
  { id: "bold", label: "強気" },
  { id: "careful", label: "慎重" },
  { id: "idea", label: "発想派" },
  { id: "realist", label: "現実派" },
  { id: "numbers", label: "数字重視" },
  { id: "user_first", label: "ユーザー至上" },
  { id: "skeptic", label: "懐疑派" },
  { id: "contrarian", label: "逆張り" },
];

export const groupTalkTemplates: GroupTalkTemplateConfig[] = [
  {
    id: "management",
    title: "経営会議",
    description: "経営判断を、成長・数字・運用の観点から多角的に整理する会議です。",
    members: ["mei", "cmo", "cfo", "coo"],
  },
  {
    id: "project",
    title: "プロジェクト会議",
    description: "プロダクトと開発、進め方をバランス良く見ながら進める会議です。",
    members: ["mei", "cto", "pm", "consultant"],
  },
  {
    id: "professional",
    title: "士業相談",
    description: "税務・法務・労務の論点を横並びで確認する会議です。",
    members: ["mei", "tax", "law", "labor"],
  },
  {
    id: "custom",
    title: "自由に組む",
    description: "メイを中心に、必要な役割を自由に追加して会議を組み立てます。",
    members: ["mei"],
  },
];

export const defaultGroupTalkStances: Record<GroupTalkMemberId, GroupTalkStanceId | null> = {
  mei: null,
  cmo: "bold",
  cto: "idea",
  cfo: "numbers",
  coo: "realist",
  pm: "user_first",
  tax: "numbers",
  law: "skeptic",
  labor: "careful",
  customer: "user_first",
  consultant: "realist",
};

export function findGroupTalkMember(memberId: GroupTalkMemberId) {
  return groupTalkMemberOptions.find((option) => option.id === memberId) ?? groupTalkMemberOptions[0];
}

export function findGroupTalkStanceLabel(stanceId: GroupTalkStanceId | null) {
  if (!stanceId) return null;
  return groupTalkStanceOptions.find((option) => option.id === stanceId)?.label ?? null;
}

export function createParticipant(memberId: GroupTalkMemberId, stanceId?: GroupTalkStanceId | null): GroupTalkParticipant {
  const member = findGroupTalkMember(memberId);
  return {
    participantId: `participant_${memberId}_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`,
    memberId,
    stanceId: typeof stanceId === "undefined" ? defaultGroupTalkStances[memberId] : stanceId,
    fixed: Boolean(member.fixed),
  };
}

export function buildParticipantsFromTemplate(templateId: GroupTalkTemplateId): GroupTalkParticipant[] {
  const template = groupTalkTemplates.find((item) => item.id === templateId) ?? groupTalkTemplates[0];
  return template.members.map((memberId) => createParticipant(memberId));
}

export function isDuplicateParticipant(
  participants: GroupTalkParticipant[],
  memberId: GroupTalkMemberId,
  stanceId: GroupTalkStanceId | null,
  ignoreParticipantId?: string
) {
  return participants.some(
    (participant) =>
      participant.participantId !== ignoreParticipantId &&
      participant.memberId === memberId &&
      participant.stanceId === stanceId
  );
}

export function buildPreviewLabel(
  memberId: GroupTalkMemberId,
  stanceId: GroupTalkStanceId | null,
  facilitatorName: string
) {
  const member = findGroupTalkMember(memberId);
  if (memberId === "mei") return facilitatorName;
  const stanceLabel = findGroupTalkStanceLabel(stanceId);
  return stanceLabel ? `${member.label}（${stanceLabel}）` : member.label;
}

export function buildGroupTalkPreviewMembers(
  participants: GroupTalkParticipant[],
  facilitatorName: string
): GroupTalkPreviewMember[] {
  return participants.map((participant) => {
    const member = findGroupTalkMember(participant.memberId);
    return {
      participantId: participant.participantId,
      memberId: participant.memberId,
      label: buildPreviewLabel(participant.memberId, participant.stanceId, facilitatorName),
      roleLabel: member.roleLabel,
      stanceId: participant.stanceId,
      stanceLabel: findGroupTalkStanceLabel(participant.stanceId),
      fixed: participant.fixed,
    };
  });
}
